import { MenuItem } from "@mui/material";
import tool_gmap from "assets/icons/estate_info/tool_gmap.svg";
import ic_menu_walk from "assets/icons/ic_menu_walk.svg";
import ic_plus from "assets/icons/ic_plus.svg";
import ic_timer from "assets/icons/isochrone/ic_timer.svg";
import ic_memo from "assets/icons/map_menu/ic_memo.svg";
import { EstateTypeNameEnum } from "common/enums/EstateTypeEnum";
import { GroupEnum } from "common/enums/GroupEnum";
import { SearchModeEnum } from "common/enums/SearchModeEnum";
import { StatusNameEnum } from "common/enums/StatusEnum";
import { MinimalEstate } from "common/queries/minimalEstates";
import { ContextMenu } from "components/Parts/Atoms/ContextMenu";
import { Company } from "graphql/API";
import React, { JSX } from "react";
import styles from "./components.module.scss";
import { RouteLocationStatus } from "./types";

export const ContextMenuOnEstate = React.memo(
  (props: {
    estateContextMenuPosition: {
      mouseX: number;
      mouseY: number;
    } | null;
    estate: MinimalEstate | null | undefined;
    editMode: boolean;
    searchMode: SearchModeEnum | undefined;
    company: Company | undefined;
    groups: string[] | undefined;
    onEstateContextMenuClose: (
      event: object,
      reason: "backdropClick" | "escapeKeyDown"
    ) => void;
    onSetRouteFrom: (() => void) | undefined;
    onAddRouteWaypoint: (() => void) | undefined;
    onSetRouteTo: (() => void) | undefined;
    onUpdateEstate: (estate: MinimalEstate) => void;
    onDeleteEstate: (estate: MinimalEstate) => void;
    onIsochrone: () => void;
  }) => {
    const items: JSX.Element[] = [];

    if (props.editMode && props.estate?.__typename === "Estate") {
      if (
        props.estate?.estateType === "？" ||
        ([
          EstateTypeNameEnum.土地,
          EstateTypeNameEnum.新築建売,
          EstateTypeNameEnum.中古戸建,
          EstateTypeNameEnum.事業用
        ].includes(props.estate?.estateType as EstateTypeNameEnum) &&
          props.estate?.blocks.length !== 0)
      ) {
        items.push(
          <MenuItem key="この物件は直接編集できません" disabled={true}>
            この物件は直接編集できません
          </MenuItem>
        );
      } else if (
        // ハウスメーカー、不動産会社は自社物件のみ編集可
        (props.groups?.includes(GroupEnum.HouseMaker) ||
          props.groups?.includes(GroupEnum.RealEstateAgency) ||
          props.groups?.includes(GroupEnum.RealEstateAgencyFree)) &&
        String(props.company?.recNo) !==
          props.estate?.companyId?.split("-")?.at(0) &&
        String(props.company?.recNo) !==
          props.estate?.subCompanyId?.split("-")?.at(0) &&
        String(props.company?.recNo) !==
          props.estate?.subCompany2Id?.split("-")?.at(0)
      ) {
        items.push(
          <MenuItem key="この物件は編集できません" disabled={true}>
            この物件は編集できません
          </MenuItem>
        );
      } else {
        items.push(
          ...[
            <MenuItem
              key={`この${props.estate?.estateType}を編集`}
              onClick={() =>
                props.onUpdateEstate(props.estate as MinimalEstate)
              }
            >
              {`この${props.estate?.estateType}を編集`}
            </MenuItem>,
            <MenuItem
              key={`この${props.estate?.estateType}を削除`}
              disabled={
                props.estate?.estateType === EstateTypeNameEnum.マンション
              }
              onClick={() =>
                props.onDeleteEstate(props.estate as MinimalEstate)
              }
            >
              {`この${props.estate?.estateType}を削除`}
            </MenuItem>
          ]
        );
      }
    } else {
      items.push(
        ...[
          <MenuItem key="出発地にする（S）" onClick={props.onSetRouteFrom}>
            <img
              src={ic_menu_walk}
              alt="出発地にする（S）"
              className={styles.icon}
            />
            出発地にする（S）
          </MenuItem>,
          <MenuItem key="目的地にする（E）" onClick={props.onSetRouteTo}>
            <img
              src={ic_menu_walk}
              alt="目的地にする（E）"
              className={styles.icon}
            />
            目的地にする（E）
          </MenuItem>,
          <MenuItem key="経由地に追加" onClick={props.onAddRouteWaypoint}>
            <img
              src={ic_menu_walk}
              alt="経由地に追加"
              className={styles.icon}
            />
            経由地に追加
          </MenuItem>
        ]
      );

      if (
        (props.groups?.every(
          (group) => group === GroupEnum.RealEstateAgencyFree
        ) ?? true) === false
      ) {
        items.push(
          <MenuItem key="到達圏を表示" onClick={props.onIsochrone}>
            <img src={ic_timer} alt="到達圏を表示" className={styles.icon} />
            到達圏を表示
          </MenuItem>
        );
      }
    }

    return (
      <ContextMenu
        contextMenuPosition={props.estateContextMenuPosition}
        onClose={props.onEstateContextMenuClose}
      >
        {items}
      </ContextMenu>
    );
  }
);

export const ContextMenuOnMap = React.memo(
  (props: {
    mapContextMenuPosition: {
      mouseX: number;
      mouseY: number;
    } | null;
    searchRouteMode: boolean;
    myPosition: google.maps.LatLngLiteral | null;
    editMode: boolean;
    searchMode: SearchModeEnum | undefined;
    stickyNoteMode: boolean | undefined;
    groups: string[] | undefined;
    onMapContextMenuClose: (
      event: object,
      reason: "backdropClick" | "escapeKeyDown"
    ) => void;
    onSetRouteFromMyPosition: (() => void) | undefined;
    onSetRouteFrom: (() => void) | undefined;
    onAddRouteWaypoint: (() => void) | undefined;
    onSetRouteTo: (() => void) | undefined;
    onCreateEstate: (
      estateType: EstateTypeNameEnum,
      status: StatusNameEnum
    ) => void;
    onCopyLinkOfGoogleMap: () => void;
    onCreateStickNote: () => void;
    onCreateRequest: () => void;
    onIsochrone: () => void;
  }) => {
    const items: JSX.Element[] = [];

    if (props.editMode) {
      if (props.searchMode === SearchModeEnum.condominium) {
        items.push(
          <MenuItem key="この画面では他の物件を追加できません" disabled={true}>
            この画面では他の物件を追加できません
          </MenuItem>
        );
      } else {
        items.push(
          ...[
            EstateTypeNameEnum.土地,
            EstateTypeNameEnum.新築建売,
            EstateTypeNameEnum.中古戸建,
            EstateTypeNameEnum.事業用
          ].map((estateType) =>
            [
              SearchModeEnum.estate as string,
              SearchModeEnum.draft as string
            ].includes(props.searchMode || "") ? (
              <MenuItem
                key={`${estateType}, ${StatusNameEnum.下書き}`}
                onClick={() =>
                  props.onCreateEstate(estateType, StatusNameEnum.下書き)
                }
              >
                ここに{estateType}を追加
              </MenuItem>
            ) : (
              <MenuItem key={`${estateType}, cannot add`} disabled={true}>
                この画面では{estateType}を追加できません
              </MenuItem>
            )
          )
        );
      }
    } else {
      if (
        (props.groups?.every(
          (group) => group === GroupEnum.RealEstateAgencyFree
        ) ?? true) === false
      ) {
        items.push(
          <MenuItem key="到達圏を表示" onClick={props.onIsochrone}>
            <img src={ic_timer} alt="到達圏を表示" className={styles.icon} />
            到達圏を表示
          </MenuItem>
        );
      }

      items.push(
        ...[
          <MenuItem key="出発地にする（S）" onClick={props.onSetRouteFrom}>
            <img
              src={ic_menu_walk}
              alt="出発地にする（S）"
              className={styles.icon}
            />
            出発地にする（S）
          </MenuItem>,
          <MenuItem key="目的地にする（E）" onClick={props.onSetRouteTo}>
            <img
              src={ic_menu_walk}
              alt="目的地にする（E）"
              className={styles.icon}
            />
            目的地にする（E）
          </MenuItem>,
          <MenuItem key="経由地に追加" onClick={props.onAddRouteWaypoint}>
            <img
              src={ic_menu_walk}
              alt="経由地に追加"
              className={styles.icon}
            />
            経由地に追加
          </MenuItem>,
          <MenuItem
            key="メモを追加"
            disabled={props.stickyNoteMode === undefined}
            onClick={props.onCreateStickNote}
          >
            <img src={ic_memo} alt="メモを追加" className={styles.icon} />
            メモを追加
          </MenuItem>,
          <MenuItem key="物件登録依頼（公開）" onClick={props.onCreateRequest}>
            <img
              src={ic_plus}
              alt="物件登録依頼（公開）"
              className={styles.icon}
            />
            物件登録依頼（公開）
          </MenuItem>,
          <MenuItem
            key="Google Mapの位置情報をコピー"
            onClick={props.onCopyLinkOfGoogleMap}
          >
            <img
              src={tool_gmap}
              alt="Google Mapの位置情報をコピー"
              className={`${styles.icon} ${styles.gmap}`}
            />
            Google Mapの位置情報をコピー
          </MenuItem>
        ]
      );
    }

    return (
      <ContextMenu
        contextMenuPosition={props.mapContextMenuPosition}
        onClose={props.onMapContextMenuClose}
      >
        {items}
      </ContextMenu>
    );
  }
);

export const ContextMenuOnRouteMarker = React.memo(
  (props: {
    position: {
      mouseX: number;
      mouseY: number;
    } | null;
    routeLocationStatus: RouteLocationStatus;
    onRouteLocationContextMenuClose: (
      event: object,
      reason: "backdropClick" | "escapeKeyDown"
    ) => void;
    onSetRouteFrom: (() => void) | undefined;
    onSetRouteWaypoint: (() => void) | undefined;
    onSetRouteTo: (() => void) | undefined;
    onDeleteRouteLocation: (() => void) | undefined;
  }) => (
    <ContextMenu
      contextMenuPosition={props.position}
      onClose={props.onRouteLocationContextMenuClose}
    >
      <MenuItem
        onClick={props.onSetRouteFrom}
        disabled={props.routeLocationStatus === "from"}
      >
        <img
          src={ic_menu_walk}
          alt="出発地に変更（S）"
          className={styles.icon}
        />
        出発地に変更（S）
      </MenuItem>
      <MenuItem
        onClick={props.onSetRouteTo}
        disabled={props.routeLocationStatus === "to"}
      >
        <img
          src={ic_menu_walk}
          alt="目的地に変更（E）"
          className={styles.icon}
        />
        目的地に変更（E）
      </MenuItem>
      <MenuItem
        onClick={props.onSetRouteWaypoint}
        disabled={props.routeLocationStatus === "waypoints"}
      >
        <img src={ic_menu_walk} alt="経由地に変更" className={styles.icon} />
        経由地に変更
      </MenuItem>
      <MenuItem onClick={props.onDeleteRouteLocation}>
        <img src={ic_menu_walk} alt="ルートから削除" className={styles.icon} />
        ルートから削除
      </MenuItem>
    </ContextMenu>
  )
);

export const ContextMenuOnSchool = React.memo(
  (props: {
    schoolContextMenuPosition: {
      mouseX: number;
      mouseY: number;
    } | null;
    groups: string[] | undefined;
    onSchoolContextMenuClose: (
      event: object,
      reason: "backdropClick" | "escapeKeyDown"
    ) => void;
    onSetRouteFrom: (() => void) | undefined;
    onAddRouteWaypoint: (() => void) | undefined;
    onSetRouteTo: (() => void) | undefined;
    onIsochrone: () => void;
  }) => {
    const items: JSX.Element[] = [];

    items.push(
      ...[
        <MenuItem key="出発地にする（S）" onClick={props.onSetRouteFrom}>
          <img
            src={ic_menu_walk}
            alt="出発地にする（S）"
            className={styles.icon}
          />
          出発地にする（S）
        </MenuItem>,
        <MenuItem key="目的地にする（E）" onClick={props.onSetRouteTo}>
          <img
            src={ic_menu_walk}
            alt="目的地にする（E）"
            className={styles.icon}
          />
          目的地にする（E）
        </MenuItem>,
        <MenuItem key="経由地に追加" onClick={props.onAddRouteWaypoint}>
          <img src={ic_menu_walk} alt="経由地に追加" className={styles.icon} />
          経由地に追加
        </MenuItem>
      ]
    );

    if (
      (props.groups?.every(
        (group) => group === GroupEnum.RealEstateAgencyFree
      ) ?? true) === false
    ) {
      items.push(
        <MenuItem key="到達圏を表示" onClick={props.onIsochrone}>
          <img src={ic_timer} alt="到達圏を表示" className={styles.icon} />
          到達圏を表示
        </MenuItem>
      );
    }

    return (
      <ContextMenu
        contextMenuPosition={props.schoolContextMenuPosition}
        onClose={props.onSchoolContextMenuClose}
      >
        {items}
      </ContextMenu>
    );
  }
);
